import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import portalApi, { setPortalToken } from '../../portalApi';
import { useLang } from '../../context/LangContext';

const STATUS = {
  conform:          { key: 'fs_conform',          color: '#1d4ed8', bg: '#dbeafe' },
  niet_conform:     { key: 'fs_niet_conform',     color: '#b91c1c', bg: '#fee2e2' },
  observatie:       { key: 'fs_observatie',       color: '#c2410c', bg: '#ffedd5' },
  kans_verbetering: { key: 'fs_kans_verbetering', color: '#15803d', bg: '#dcfce7' },
  nvt:              { key: 'fs_nvt',              color: '#6b7280', bg: '#f1f5f9' },
  niet_beoordeeld:  { key: 'fs_niet_beoordeeld',  color: '#6b7280', bg: '#f1f5f9' },
};

function ReportView({ audit, findings, t }) {
  const sections = [];
  const bySection = {};
  findings.forEach(f => {
    if (!bySection[f.section]) { bySection[f.section] = []; sections.push(f.section); }
    bySection[f.section].push(f);
  });

  return (
    <div>
      <div className="mb-4">
        <h3 className="mb-1">{audit.name}</h3>
        <div className="text-muted small">
          {audit.client_name} · {audit.framework_name}{audit.framework_version ? ` ${audit.framework_version}` : ''}
          {audit.audit_date ? ` · ${new Date(audit.audit_date).toLocaleDateString()}` : ''}
          {audit.auditor_name ? ` · ${audit.auditor_name}` : ''}
          {` · v${audit.version_major}.${audit.version_minor}`}
        </div>
        {audit.scope && (
          <div className="small mt-2"><strong>{t('portal_scope')}:</strong> {audit.scope}</div>
        )}
      </div>

      {sections.map(sec => (
        <div key={sec} className="mb-4">
          <h5 className="border-bottom pb-1">{sec}</h5>
          {bySection[sec].map((f, i) => {
            const cfg = STATUS[f.status] || STATUS.niet_beoordeeld;
            return (
              <div key={i} className="mb-3 pb-2" style={{ borderBottom: '1px solid #f1f5f9' }}>
                <div className="d-flex justify-content-between align-items-start gap-2">
                  <div className="fw-semibold">
                    <span className="font-mono text-muted me-2">{f.control_id}</span>{f.title}
                  </div>
                  <span className="badge flex-shrink-0" style={{ background: cfg.bg, color: cfg.color }}>{t(cfg.key)}</span>
                </div>
                {f.current_situation && <div className="small mt-1"><em>{t('finding_situation')}:</em> {f.current_situation}</div>}
                {f.finding          && <div className="small mt-1"><em>{t('finding_finding')}:</em> {f.finding}</div>}
                {f.recommendation   && <div className="small mt-1"><em>{t('finding_recommendation')}:</em> {f.recommendation}</div>}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export default function PortalReportPage() {
  const { token, shareId } = useParams();
  const { t, lang, switchLang } = useLang();
  const [state, setState]     = useState('loading'); // loading | need_code | ready | error
  const [report, setReport]   = useState(null);
  const [error, setError]     = useState('');
  const [code, setCode]       = useState('');
  const [hasPortal, setHasPortal] = useState(false);

  const loadByToken = useCallback(async (codeVal) => {
    setState('loading'); setError('');
    try {
      const res = await portalApi.post(`/api/portal/report/${token}`, codeVal ? { code: codeVal } : {});
      setReport(res.data);
      if (res.data.portalToken) { setPortalToken(res.data.portalToken); setHasPortal(true); }
      setState('ready');
    } catch (err) {
      if (err.response?.status === 401 && err.response.data?.needCode) {
        setState('need_code');
        setError(err.response.data.error || '');
      } else {
        setError(err.response?.data?.error || t('error'));
        setState('error');
      }
    }
  }, [token, t]);

  const loadByShare = useCallback(async () => {
    setState('loading'); setError('');
    try {
      const res = await portalApi.get(`/api/portal/reports/${shareId}`);
      setReport(res.data);
      setHasPortal(true);
      setState('ready');
    } catch (err) {
      setError(err.response?.data?.error || t('error'));
      setState('error');
    }
  }, [shareId, t]);

  useEffect(() => {
    if (token) loadByToken();
    else if (shareId) loadByShare();
  }, [token, shareId, loadByToken, loadByShare]);

  return (
    <div style={{ maxWidth: 820, margin: '0 auto', padding: '24px 16px' }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <img src="/logo-fdconsult.png" alt="FDConsult" style={{ height: 40 }} />
        <div className="d-flex align-items-center gap-3">
          {hasPortal && <Link to="/portal" className="small text-decoration-none">{t('portal_my_reports')}</Link>}
          <div className="lang-toggle">
            <button className={lang === 'nl' ? 'active' : ''} onClick={() => switchLang('nl')}>NL</button>
            <button className={lang === 'en' ? 'active' : ''} onClick={() => switchLang('en')}>EN</button>
          </div>
        </div>
      </div>

      {state === 'loading' && (
        <div className="text-center text-muted py-5"><span className="spinner-border spinner-border-sm me-2" />{t('loading')}</div>
      )}
      {state === 'error' && <div className="alert alert-danger">{error}</div>}
      {state === 'need_code' && (
        <div className="card"><div className="card-body">
          <h5 className="mb-1">{t('portal_code_title')}</h5>
          <div className="text-muted small mb-3">{t('portal_code_intro')}</div>
          {error && <div className="alert alert-danger py-2">{error}</div>}
          <form onSubmit={(e) => { e.preventDefault(); loadByToken(code); }} className="d-flex gap-2">
            <input className="form-control" value={code} onChange={e => setCode(e.target.value)} autoFocus />
            <button className="btn btn-primary">{t('portal_code_submit')}</button>
          </form>
        </div></div>
      )}
      {state === 'ready' && report && <ReportView audit={report.audit} findings={report.findings} t={t} />}
    </div>
  );
}
